var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznTextarea_instances, _HznTextarea_emit, _HznTextarea_onInvalid, _HznTextarea_setTextareaHeight, _HznTextarea_handleInput, _HznTextarea_handleChange, _HznTextarea_renderCharacterCounter;
// class definition
import { html, LitElement, nothing } from '@horizon/base';
import { property, query, state } from '@horizon/base/decorators';
import { classMap, ifDefined, live } from '@horizon/base/directives';
import { eventEmitter } from '@horizon/common/events';
import { maxLengthValidator, minLengthValidator, requiredValidator, customValidator } from '@horizon/common/mixins';
import { FormControlMixin } from '@open-wc/form-control';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import 'element-internals-polyfill';
import { HznInline } from '@horizon/inline';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import InputStyles from './textarea.css.js';
/**
 *
 * @tag hzn-textarea
 * @tagname hzn-textarea
 * @summary An textarea input for entering larger sections of text
 *
 * @fires {CustomEvent<{}>} input - Emitted every time a character is typed into the textarea
 * @fires {CustomEvent<{}>} change - Emitted when the texarea is changed
 */
export class HznTextarea extends FormControlMixin(ScopedElementsMixin(LitElement)) {
    static get scopedElements() {
        return {
            'hzn-stack': HznStack,
            'hzn-text': HznText,
            'hzn-inline': HznInline,
        };
    }
    static get formControlValidators() {
        return [
            requiredValidator,
            minLengthValidator,
            maxLengthValidator,
            customValidator
        ];
    }
    /**
     * @private
     */
    get characterCount() {
        return this.value ? this.value.length : 0;
    }
    constructor() {
        super();
        _HznTextarea_instances.add(this);
        /**
         * @private
         */
        _HznTextarea_emit.set(this, eventEmitter(this));
        /**
         * Whether or not the textarea is compact size
         */
        this.compact = false;
        /**
         * Sets the textarea value
         */
        this.value = '';
        /**
         * Set whether or not the textarea is required to have a value
         */
        this.required = false;
        /**
         * Set the textarea to be disabled
         */
        this.disabled = false;
        /**
         * Set the textarea to be disabled
         */
        this.readonly = false;
        /**
         * Set the help text for the textarea
         */
        this.helperText = '';
        /**
         * Hides the help text for the textarea
         */
        this.hideHelperText = false;
        /**
         * Set the help text for the textarea
         * @playroomValues {'none' | 'vertical' | 'content'}
         */
        this.resize = 'vertical';
        /**
         * Whether or not to visually hide the textarea's label. A label should always be present even when hidden
         */
        this.hideLabel = false;
        /**
         * @private
         */
        _HznTextarea_onInvalid.set(this, (event) => {
            var _a;
            event.preventDefault();
            (_a = this.validationTarget) === null || _a === void 0 ? void 0 : _a.focus();
        });
        this.addEventListener('invalid', __classPrivateFieldGet(this, _HznTextarea_onInvalid, "f"));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', __classPrivateFieldGet(this, _HznTextarea_onInvalid, "f"));
    }
    /**
     * @private
     */
    resetFormControl() {
        this.value = '';
    }
    /**
     * @private
     */
    get showErrors() {
        // only show the clear button if clearable, with a value
        // and not disabled or readonly
        return this.showError && !this.disabled && !this.readonly;
    }
    /**
     * @private
     */
    validationMessageCallback(message) {
        this.internalErrorMessage = message;
    }
    /**
     * @private
     */
    updated(changed) {
        if (changed.has('value')) {
            this.setValue(this.value);
        }
    }
    /** Sets focus on the input. */
    focus(options) {
        this.validationTarget.focus(options);
    }
    /** Removes focus from the input. */
    blur() {
        this.validationTarget.blur();
    }
    render() {
        const textareaStyle = {
            compact: Boolean(this.compact),
            'resize-none': this.resize === 'none',
            'resize-vertical': this.resize === 'vertical',
            'resize-content': this.resize === 'content',
            error: this.showErrors,
        };
        return html `<hzn-stack space="xsmall">
      <label id="label" for="input" class="${this.hideLabel ? 'visually-hidden' : ''}">
        <hzn-text variant="text">
        <slot>${this.displayName || this.name || 'Textarea'}</slot>
        </hzn-text>
      </label>

      <textarea
        class="${classMap(textareaStyle)}"
        title=""
        id="input"
        name=${ifDefined(this.name)}
        .value=${live(this.value)}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?required=${this.required}
        placeholder=${ifDefined(this.placeholder)}
        rows=${ifDefined(this.rows)}
        minlength=${ifDefined(this.minlength)}
        maxlength=${ifDefined(this.maxlength)}
        autocomplete=${ifDefined(this.autocomplete)}
        autocapitalize=${ifDefined(this.autocapitalize)}
        autocorrect=${ifDefined(this.autocorrect)}
        ?autofocus=${this.autofocus}
        spellcheck=${ifDefined(this.spellcheck ? this.spellCheck : undefined)}
        enterkeyhint=${ifDefined(this.enterkeyhint)}
        inputmode=${ifDefined(this.inputmode)}
        aria-labelledby="${this.helperText ? 'label helper-text' : ''}"
        aria-describedby="${this.showCharacterCount && !!this.maxlength ? 'character-count' : ''}"
        @input=${__classPrivateFieldGet(this, _HznTextarea_instances, "m", _HznTextarea_handleInput)}
        @change=${__classPrivateFieldGet(this, _HznTextarea_instances, "m", _HznTextarea_handleChange)}
      ></textarea>

      ${__classPrivateFieldGet(this, _HznTextarea_instances, "m", _HznTextarea_renderCharacterCounter).call(this)}
      ${this.showErrors
            ? html `<hzn-text role="alert" id="error-message" class="error-message" variant="caption" tone="critical"
            >${this.internalErrorMessage}</hzn-text
          >`
            : nothing}
    </hzn-stack>`;
    }
}
_HznTextarea_emit = new WeakMap(), _HznTextarea_onInvalid = new WeakMap(), _HznTextarea_instances = new WeakSet(), _HznTextarea_setTextareaHeight = function _HznTextarea_setTextareaHeight() {
    if (this.resize === 'content') {
        this.validationTarget.style.height = 'auto';
        this.validationTarget.style.height = `${this.validationTarget.scrollHeight}px`;
    }
    else {
        this.validationTarget.style.height = undefined;
    }
}, _HznTextarea_handleInput = function _HznTextarea_handleInput(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.value = event.target.value;
    __classPrivateFieldGet(this, _HznTextarea_instances, "m", _HznTextarea_setTextareaHeight).call(this);
    __classPrivateFieldGet(this, _HznTextarea_emit, "f").call(this, { type: 'input' });
}, _HznTextarea_handleChange = function _HznTextarea_handleChange(event) {
    event.stopImmediatePropagation();
    event.preventDefault();
    __classPrivateFieldGet(this, _HznTextarea_emit, "f").call(this, { type: 'change' });
}, _HznTextarea_renderCharacterCounter = function _HznTextarea_renderCharacterCounter() {
    if (this.showCharacterCount && !!this.maxlength && this.helperText) {
        return html `<hzn-inline>
        <hzn-text
          class="${'flex-grow' + (this.hideHelperText ? ' visually-hidden' : '')}"
          id="helper-text"
          variant="caption"
          tone="subdued"
        >
          ${this.helperText}
        </hzn-text>
        <hzn-text id="character-count" class="character-count" variant="caption" tone="subdued">
          <span aria-hidden="true">${this.characterCount}/${this.maxlength}</span>
          <span class="visually-hidden">${this.maxlength - this.characterCount} characters remaining.</span>
        </hzn-text>
      </hzn-inline>`;
    }
    else if (this.showCharacterCount && !!this.maxlength && !this.helperText) {
        return html `<hzn-inline align-x="end">
        <hzn-text id="character-count" class="character-count" variant="caption" tone="subdued">
          <span aria-hidden="true">${this.characterCount}/${this.maxlength}</span>
          <span class="visually-hidden">${this.maxlength - this.characterCount} characters remaining.</span>
        </hzn-text>
      </hzn-inline>`;
    }
    else if ((!this.showCharacterCount || !this.maxlength) && this.helperText) {
        return html ` <hzn-text
        class="${this.hideHelperText ? 'visually-hidden' : ''}"
        id="helper-text"
        variant="caption"
        tone="subdued"
      >
        ${this.helperText}
      </hzn-text>`;
    }
    return nothing;
};
HznTextarea.styles = [InputStyles];
__decorate([
    query('textarea')
], HznTextarea.prototype, "innerTextarea", void 0);
__decorate([
    state()
], HznTextarea.prototype, "internalErrorMessage", void 0);
__decorate([
    query('textarea')
], HznTextarea.prototype, "validationTarget", void 0);
__decorate([
    property()
], HznTextarea.prototype, "name", void 0);
__decorate([
    property({ type: Boolean })
], HznTextarea.prototype, "compact", void 0);
__decorate([
    property({ type: String, reflect: true })
], HznTextarea.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
], HznTextarea.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznTextarea.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznTextarea.prototype, "readonly", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], HznTextarea.prototype, "helperText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-helper-text' })
], HznTextarea.prototype, "hideHelperText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-character-count' })
], HznTextarea.prototype, "showCharacterCount", void 0);
__decorate([
    property({ type: String })
], HznTextarea.prototype, "resize", void 0);
__decorate([
    property({ type: Number })
], HznTextarea.prototype, "rows", void 0);
__decorate([
    property({ type: Number })
], HznTextarea.prototype, "minlength", void 0);
__decorate([
    property({ type: Number })
], HznTextarea.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label' })
], HznTextarea.prototype, "hideLabel", void 0);
__decorate([
    property()
], HznTextarea.prototype, "placeholder", void 0);
__decorate([
    property()
], HznTextarea.prototype, "autocorrect", void 0);
__decorate([
    property()
], HznTextarea.prototype, "autocomplete", void 0);
__decorate([
    property()
], HznTextarea.prototype, "autocapitalize", void 0);
__decorate([
    property({ type: Boolean })
], HznTextarea.prototype, "autofocus", void 0);
__decorate([
    property()
], HznTextarea.prototype, "enterkeyhint", void 0);
__decorate([
    property({ type: String, attribute: 'spellcheck' })
], HznTextarea.prototype, "spellCheck", void 0);
__decorate([
    property()
], HznTextarea.prototype, "inputmode", void 0);
__decorate([
    property({ type: String, attribute: 'display-name' })
], HznTextarea.prototype, "displayName", void 0);
__decorate([
    property({ attribute: false })
], HznTextarea.prototype, "errorMessages", void 0);
__decorate([
    property({ type: String, attribute: false })
], HznTextarea.prototype, "validation", void 0);
//# sourceMappingURL=textarea.js.map